import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LogOut, Trash2, Users, Wine, Crown, Hourglass, Mail, Send, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const inputCls =
  "w-full bg-[#0A0908] border border-[rgba(212,175,55,0.2)] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4F1EA] placeholder:text-[#6E685E] transition-colors";

const LoginForm = ({ onLogin }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/login", form);
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0908] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <p className="overline-label text-center">Cellar Door</p>
        <h1 className="font-serif-display text-4xl font-light text-[#F4F1EA] text-center mt-3">Admin</h1>
        <form data-testid="admin-login-form" onSubmit={submit} className="mt-8 flex flex-col gap-4 bg-[#141210] border border-[rgba(212,175,55,0.2)] p-8">
          <input
            data-testid="admin-login-email"
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
          />
          <input
            data-testid="admin-login-password"
            required
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputCls}
          />
          {error && <p data-testid="admin-login-error" className="text-sm text-[#C86D51]">{error}</p>}
          <button
            data-testid="admin-login-submit"
            disabled={busy}
            className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D4AF37] text-[#0A0908] py-4 hover:bg-[#F4F1EA] transition-colors disabled:opacity-50"
          >
            {busy ? "Opening…" : "Enter the Cellar"}
          </button>
        </form>
        <Link to="/" data-testid="admin-back-home" className="block text-center mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E685E] hover:text-[#D4AF37] transition-colors">
          Back to the estate
        </Link>
      </motion.div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-[#141210] border border-[rgba(212,175,55,0.15)] p-6">
    <div className="flex items-center justify-between">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E685E]">{label}</p>
      <Icon size={15} className="text-[#D4AF37]" />
    </div>
    <p className="font-serif-display text-4xl font-light text-[#F4F1EA] mt-3">{value}</p>
  </div>
);

const selectCls =
  "bg-[#0A0908] border border-[rgba(212,175,55,0.2)] px-2 py-1.5 text-xs text-[#F4F1EA] outline-none focus:border-[#D4AF37]";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const MembersTab = ({ members, onChanged, onMessage }) => {
  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/members/${id}`, patch);
      toast.success("Member updated");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/members/${id}`);
      toast.success("Member removed");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-[#141210] border border-[rgba(212,175,55,0.15)] overflow-x-auto">
      <table data-testid="admin-members-table" className="w-full text-sm min-w-[760px]">
        <thead>
          <tr className="border-b border-[rgba(212,175,55,0.15)]">
            {["Member", "Contact", "Tier", "Status", "Joined", ""].map((h) => (
              <th key={h} className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E685E] px-5 py-4">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {members.length === 0 && (
            <tr>
              <td colSpan={6} data-testid="members-empty-state" className="px-5 py-12 text-center text-[#6E685E] text-sm">
                No members yet — share the site and watch the cellar fill.
              </td>
            </tr>
          )}
          {members.map((m) => (
            <tr key={m.id} data-testid={`member-row-${m.email}`} className="border-b border-[rgba(212,175,55,0.08)] hover:bg-[#0A0908]/50 transition-colors">
              <td className="px-5 py-4 text-[#F4F1EA]">{m.name}</td>
              <td className="px-5 py-4 text-[#A39E93]">
                <div>{m.email}</div>
                {m.phone && <div className="text-xs text-[#6E685E]">{m.phone}</div>}
              </td>
              <td className="px-5 py-4">
                <select
                  data-testid={`member-tier-select-${m.email}`}
                  value={m.tier}
                  onChange={(e) => update(m.id, { tier: e.target.value })}
                  className={selectCls}
                >
                  <option value="breeze">Breeze</option>
                  <option value="reserve">Reserve</option>
                </select>
              </td>
              <td className="px-5 py-4">
                <select
                  data-testid={`member-status-select-${m.email}`}
                  value={m.status}
                  onChange={(e) => update(m.id, { status: e.target.value })}
                  className={selectCls}
                >
                  <option value="active">Active</option>
                  <option value="pending_payment">Pending</option>
                </select>
              </td>
              <td className="px-5 py-4 text-[#6E685E] text-xs">{fmtDate(m.created_at)}</td>
              <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                  <button
                    data-testid={`member-message-${m.email}`}
                    onClick={() => onMessage(m)}
                    className="text-[#6E685E] hover:text-[#D4AF37] transition-colors"
                    aria-label={`Message ${m.name}`}
                  >
                    <Mail size={15} />
                  </button>
                  <button
                    data-testid={`member-delete-${m.email}`}
                    onClick={() => remove(m.id)}
                    className="text-[#6E685E] hover:text-[#C86D51] transition-colors"
                    aria-label={`Remove ${m.name}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AudienceTab = ({ audience }) => {
  if (!audience) return null;
  const Section = ({ title, rows, cols, testid }) => (
    <div className="bg-[#141210] border border-[rgba(212,175,55,0.15)] p-6" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-4">{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-[#6E685E]">Nothing here yet.</p>
      ) : (
        <div className="flex flex-col gap-3 max-h-80 overflow-y-auto pr-2">
          {rows.map((r, i) => (
            <div key={i} className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[rgba(212,175,55,0.08)] pb-3">
              <div>
                <p className="text-sm text-[#F4F1EA]">{cols.map((c) => r[c]).filter(Boolean).join(" · ")}</p>
                <p className="text-xs text-[#6E685E]">{r.email}</p>
              </div>
              <p className="text-xs text-[#6E685E]">{fmtDate(r.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Section title="Tasting Requests" rows={audience.reservations} cols={["name", "date", "guests"]} testid="audience-reservations" />
      <Section title="Event RSVPs" rows={audience.rsvps} cols={["name", "event"]} testid="audience-rsvps" />
      <Section title="Newsletter" rows={audience.newsletter} cols={[]} testid="audience-newsletter" />
    </div>
  );
};

const MessagesTab = ({ members, prefill, onSent }) => {
  const [audience, setAudience] = useState("all_members");
  const [memberId, setMemberId] = useState("");
  const [message, setMessage] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadLog = useCallback(() => {
    api.get("/admin/messages").then(({ data }) => setLog(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadLog();
  }, [loadLog]);

  useEffect(() => {
    if (prefill) {
      setAudience("member");
      setMemberId(prefill.id);
    }
  }, [prefill]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/messages", {
        audience,
        member_id: audience === "member" ? memberId : undefined,
        message,
      });
      toast.success(`Sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}${data.failed ? ` (${data.failed} failed)` : ""}`);
      setMessage("");
      loadLog();
      onSent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <form data-testid="admin-message-form" onSubmit={send} className="bg-[#141210] border border-[rgba(212,175,55,0.15)] p-6 flex flex-col gap-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Compose</p>
        <div className="grid grid-cols-2 gap-4">
          <select data-testid="message-audience-select" value={audience} onChange={(e) => setAudience(e.target.value)} className={selectCls}>
            <option value="all_members">All club members</option>
            <option value="breeze">Breeze Club only</option>
            <option value="reserve">Cellar Reserve only</option>
            <option value="newsletter">Newsletter subscribers</option>
            <option value="member">One member…</option>
          </select>
          {audience === "member" && (
            <select data-testid="message-member-select" value={memberId} onChange={(e) => setMemberId(e.target.value)} required className={selectCls}>
              <option value="">Choose member</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} — {m.email}
                </option>
              ))}
            </select>
          )}
        </div>
        <textarea
          data-testid="message-body-input"
          required
          rows={7}
          maxLength={2000}
          placeholder="Write your note — it arrives in a branded Delta Breeze email with the subject 'A note from Delta Breeze Winery'."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`${inputCls} resize-none`}
        />
        <button
          data-testid="message-send-btn"
          disabled={busy}
          className="flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-[#D4AF37] text-[#0A0908] py-4 hover:bg-[#F4F1EA] transition-colors disabled:opacity-50"
        >
          <Send size={13} /> {busy ? "Sending…" : "Send"}
        </button>
        <p className="font-mono text-[10px] tracking-[0.15em] text-[#6E685E]">
          Replies go to info@deltabreezewinery.com · Limit 20 sends / day, 200 recipients / send
        </p>
      </form>

      <div className="bg-[#141210] border border-[rgba(212,175,55,0.15)] p-6" data-testid="messages-log">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Sent</p>
        {log.length === 0 ? (
          <p className="text-sm text-[#6E685E]">No messages sent yet.</p>
        ) : (
          <div className="flex flex-col gap-3 max-h-96 overflow-y-auto pr-2">
            {log.map((l, i) => (
              <div key={i} className="border-b border-[rgba(212,175,55,0.08)] pb-3">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm text-[#F4F1EA] capitalize">{l.audience.replace("_", " ")} · {l.sent}/{l.recipients} sent</p>
                  <p className="text-xs text-[#6E685E]">{fmtDate(l.sent_at)}</p>
                </div>
                <p className="text-xs text-[#6E685E] mt-1 line-clamp-2">{l.preview}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [audience, setAudience] = useState(null);
  const [tab, setTab] = useState("members");
  const [prefill, setPrefill] = useState(null);

  const load = useCallback(async () => {
    const [m, s, a] = await Promise.all([
      api.get("/admin/members"),
      api.get("/admin/stats"),
      api.get("/admin/audience"),
    ]);
    setMembers(m.data);
    setStats(s.data);
    setAudience(a.data);
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load admin data"));
  }, [load]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
  };

  const TABS = [
    { id: "members", label: "Members", icon: Users },
    { id: "audience", label: "Registrants", icon: Inbox },
    { id: "messages", label: "Messages", icon: Mail },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0908] px-4 sm:px-8 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="overline-label">Cellar Door</p>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-light text-[#F4F1EA] mt-2">Club Members</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site" className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E685E] hover:text-[#D4AF37] transition-colors">
              View Site
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] border border-[rgba(212,175,55,0.3)] text-[#A39E93] px-4 py-2.5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Members" value={stats.members} testid="stat-total-members" />
            <StatCard icon={Wine} label="Breeze (Free)" value={stats.breeze} testid="stat-breeze" />
            <StatCard icon={Crown} label="Cellar Reserve" value={stats.reserve} testid="stat-reserve" />
            <StatCard icon={Hourglass} label="Pending Payment" value={stats.pending} testid="stat-pending" />
          </div>
        )}

        <div className="flex gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-5 py-3 border transition-colors ${
                tab === t.id
                  ? "border-[#D4AF37] text-[#D4AF37]"
                  : "border-[rgba(212,175,55,0.15)] text-[#6E685E] hover:text-[#A39E93]"
              }`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "members" && (
          <MembersTab
            members={members}
            onChanged={load}
            onMessage={(m) => {
              setPrefill(m);
              setTab("messages");
            }}
          />
        )}
        {tab === "audience" && <AudienceTab audience={audience} />}
        {tab === "messages" && <MessagesTab members={members} prefill={prefill} onSent={() => setPrefill(null)} />}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#0A0908] flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#6E685E]">Opening the cellar…</p>
      </div>
    );
  }
  if (!user) return <LoginForm onLogin={setUser} />;
  return <Dashboard onLogout={() => setUser(null)} />;
}
